// PRD.327 — the Visit Note header's `⋮` menu.
//
// Replaces the standalone Settings and Delete icons and adds the print actions.
// Purely presentational: every action is injected, so this component holds no
// permission logic of its own and cannot drift from the page's existing gates.

import { useEffect, useRef, useState } from 'react';
import { NavIcon } from '../../../shell/shared';
import {
  PRESCRIPTION_PRINT_PURPOSE_LABELS,
  type PrescriptionPrintPurpose,
} from '@helios/data-access';

export function VisitNoteMoreMenu({
  onTabSettings,
  onDelete,
  onPrint,
}: {
  onTabSettings: () => void;
  onDelete: () => void;
  onPrint: (purpose: PrescriptionPrintPurpose) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const run = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="More actions"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          width: 32,
          height: 32,
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border)',
          background: 'var(--bg-surface)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted)',
        }}
      >
        <NavIcon name="more-vertical" />
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            minWidth: 216,
            padding: 'var(--sp-2)',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md, 0 4px 16px rgba(0,0,0,0.12))',
            zIndex: 40,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <MenuItem icon="gear" label="Tab Settings" onClick={run(onTabSettings)} />

          <MenuHeading>Print</MenuHeading>
          <MenuItem
            icon="printer"
            label={PRESCRIPTION_PRINT_PURPOSE_LABELS.consultation_sheet}
            onClick={run(() => onPrint('consultation_sheet'))}
          />
          <MenuItem
            icon="printer"
            label={PRESCRIPTION_PRINT_PURPOSE_LABELS.prescription}
            onClick={run(() => onPrint('prescription'))}
          />
          {/* No Invoice entry: invoice printing lives with the invoice, in the
              Billing panel, which owns its own print settings and its
              flush-before-print. A menu item that only opened that panel was
              not a print action, so it is better absent than misleading. */}

          <div style={{ height: 1, background: 'var(--border)', margin: 'var(--sp-2) 0' }} />

          <MenuItem icon="trash" label="Delete Visit" onClick={run(onDelete)} destructive />
        </div>
      )}
    </div>
  );
}

function MenuHeading({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '6px 8px 2px',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: 'var(--text-muted)',
      }}
    >
      {children}
    </div>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  destructive = false,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <button
      role="menuitem"
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '7px 8px',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        background: 'transparent',
        // Destructive treatment is preserved from the old standalone icon.
        color: destructive ? '#dc2626' : 'var(--text)',
        fontSize: 'var(--font-size-sm)',
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <NavIcon name={icon} />
      <span style={{ flex: 1 }}>{label}</span>
    </button>
  );
}
